// src/pages/login/biometric-login/biometric-login.hooks.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { startAuthentication } from "@simplewebauthn/browser";
import { API_BASE_URL } from "../../../env";
import { readStoredSession } from "../../../api/token-storage";
import { detectMobilePlatform, getBiometricName, getPlatformName } from "../../../helpers/biometric";
import { BiometricsStorage } from "../../../helpers/biometrics-storage";
import { isNative } from "../../../platform";
import {
    isNativeBiometricAvailable,
    promptNativeBiometric,
    getNativeCredentials,
} from "../../../helpers/native-biometric";

type UseBiometricLoginArgs = {
    autoStart?: boolean;
    onSuccess?: (payload: VerifySuccessPayload) => void | Promise<void>;
    diagId?: string;

    /**
     * If true, will try conditional mediation (useBrowserAutofill) when available.
     * IMPORTANT: Only works reliably when an eligible input is present AND focused.
     * For an auto-start biometric screen, set this to false to avoid "hang" behavior.
     * Mobile platforms (iOS/Android) should always set this to false.
     */
    preferConditionalUI?: boolean;

    /**
     * Guard against a "hung" WebAuthn prompt path (common with conditional UI).
     * Mobile devices need longer timeouts (30-45 seconds) due to biometric prompt delays.
     */
    promptTimeoutMs?: number;
};

type VerifySuccessPayload = {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    user?: any;
    raw?: any;
};

function makeReqId(prefix = "webauthn_auth") {
    return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function safeErr(e: unknown) {
    if (axios.isAxiosError(e)) {
        return {
            kind: "axios" as const,
            message: e.message,
            status: e.response?.status,
            data: e.response?.data,
            headers: e.response?.headers,
            url: e.config?.url,
            method: e.config?.method,
        };
    }
    if (e instanceof Error) {
        return { kind: "error" as const, message: e.message };
    }
    return { kind: "unknown" as const, message: String(e) };
}

async function probeUvpa(): Promise<boolean | null> {
    try {
        if (
            typeof window !== "undefined" &&
            "PublicKeyCredential" in window &&
            typeof PublicKeyCredential
                    .isUserVerifyingPlatformAuthenticatorAvailable ===
                "function"
        ) {
            return await PublicKeyCredential
                .isUserVerifyingPlatformAuthenticatorAvailable();
        }
    } catch {
        // ignore
    }
    return null;
}

async function probeConditionalMediation(): Promise<boolean | null> {
    try {
        if (
            typeof window !== "undefined" &&
            "PublicKeyCredential" in window &&
            typeof (PublicKeyCredential as any)
                    .isConditionalMediationAvailable === "function"
        ) {
            return await (PublicKeyCredential as any)
                .isConditionalMediationAvailable();
        }
    } catch {
        // ignore
    }
    return null;
}

const SS_LAST_ATTEMPT = "bio_login_last_attempt_ts";
const SS_BLOCK_UNTIL = "bio_login_block_until_ts";

function nowMs() {
    return Date.now();
}

function getSsNumber(key: string): number | null {
    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

function setSsNumber(key: string, value: number) {
    try {
        window.sessionStorage.setItem(key, String(value));
    } catch {
        // ignore
    }
}

function computeBackoffMsFrom429(headers: any): number {
    const ra = headers?.["retry-after"] ?? headers?.["Retry-After"];
    if (typeof ra === "string" && ra.trim()) {
        const asNum = Number(ra);
        if (Number.isFinite(asNum) && asNum > 0) {
            return Math.min(120_000, asNum * 1000);
        }
    }
    return 30_000;
}

function hasFocusedWebauthnInput(): boolean {
    if (typeof document === "undefined") return false;
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement)) return false;

    // browser normalizes this; keep it defensive
    const ac =
        (active.getAttribute("autocomplete") ?? active.autocomplete ?? "")
            .trim();
    if (!ac) return false;

    const parts = ac.split(/\s+/).filter(Boolean);
    return parts.length > 0 && parts[parts.length - 1] === "webauthn";
}

function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
    let t: number | undefined;
    const timeout = new Promise<T>((_, rej) => {
        t = window.setTimeout(() => rej(new Error(msg)), ms);
    });
    return Promise.race([p, timeout]).finally(() => {
        if (t) window.clearTimeout(t);
    }) as Promise<T>;
}

export function useBiometricLogin(args: UseBiometricLoginArgs) {
    const {
        autoStart = false,
        onSuccess,
        diagId: externalDiagId,
        preferConditionalUI = true,
        promptTimeoutMs, // Allow override but default based on platform below
    } = args;

    const diagId = useMemo(() => externalDiagId ?? makeReqId("bio_login"), [
        externalDiagId,
    ]);

    // Detect mobile platform for specific handling
    const platform = useMemo(() => detectMobilePlatform(), []);
    
    // X3: Platform-appropriate timeouts
    // Android needs longer (biometric dialog can take 5-15s to spawn)
    // iOS needs moderate time, desktop is fastest
    const effectiveTimeout = promptTimeoutMs ?? (platform.isAndroid ? 25_000 : platform.isIos ? 20_000 : 15_000);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [diag, setDiag] = useState(() => ({
        diagId,
        step: "init",
        href: window.location.href,
        origin: window.location.origin,
        hostname: window.location.hostname,
        isSecureContext: window.isSecureContext,
        platformAvailable: null as boolean | null,
        conditionalMediationAvailable: null as boolean | null,
        userAgent: navigator.userAgent,
        baseUrl: (() => {
            try {
                return API_BASE_URL;
            } catch {
                return undefined;
            }
        })(),
        lastRequest: null as
            | { url: string; method: string; ts: string; status?: number }
            | null,
    }));

    const [successPayload, setSuccessPayload] = useState<
        VerifySuccessPayload | null
    >(null);

    const inFlightRef = useRef(false);
    const didAutoStartRef = useRef(false);
    const mountedRef = useRef(true);
    const abortRef = useRef<AbortController | null>(null);

    const safeSetLoading = useCallback((v: boolean) => {
        if (!mountedRef.current) return;
        setLoading(v);
    }, []);

    const safeSetError = useCallback((v: string | null) => {
        if (!mountedRef.current) return;
        setError(v);
    }, []);

    const safeSetDiag = useCallback((updater: any) => {
        if (!mountedRef.current) return;
        setDiag(updater);
    }, []);

    const safeSetSuccessPayload = useCallback(
        (v: VerifySuccessPayload | null) => {
            if (!mountedRef.current) return;
            setSuccessPayload(v);
        },
        [],
    );

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            try {
                abortRef.current?.abort();
            } catch {
                // ignore
            } finally {
                abortRef.current = null;
            }
        };
    }, []);

    const setStep = useCallback(
        (step: string) => {
            safeSetDiag((d: any) => ({ ...d, step }));
        },
        [safeSetDiag],
    );

    const buildDiag = useCallback(async () => {
        const [uvpa, cma] = await Promise.all([
            probeUvpa(),
            probeConditionalMediation(),
        ]);
        return {
            ts: new Date().toISOString(),
            href: window.location.href,
            origin: window.location.origin,
            hostname: window.location.hostname,
            ua: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            isSecureContext: window.isSecureContext,
            isFramed: window.self !== window.top,
            uvpa,
            conditionalMediationAvailable: cma,
            isIos: platform.isIos,
            isAndroid: platform.isAndroid,
            isMobile: platform.isMobile,
        };
    }, [platform]);

    const run = useCallback(async () => {
        if (inFlightRef.current) return;

        const blockedUntil = getSsNumber(SS_BLOCK_UNTIL);
        const tNow = nowMs();
        if (blockedUntil && blockedUntil > tNow) {
            const seconds = Math.ceil((blockedUntil - tNow) / 1000);
            setStep("rate-limited-local");
            safeSetError(`Too many requests. Try again in ${seconds}s.`);
            return;
        }

        const lastAttempt = getSsNumber(SS_LAST_ATTEMPT);
        if (lastAttempt && tNow - lastAttempt < 2500) {
            setStep("throttled");
            safeSetError("Please wait a moment and try again.");
            return;
        }
        setSsNumber(SS_LAST_ATTEMPT, tNow);

        inFlightRef.current = true;

        try {
            abortRef.current?.abort();
        } catch {
            // ignore
        }
        const controller = new AbortController();
        abortRef.current = controller;

        const reqId = makeReqId();
        const baseUrl = API_BASE_URL;

        safeSetLoading(true);
        safeSetError(null);
        safeSetSuccessPayload(null);

        try {
            safeSetDiag((d: any) => ({ ...d, baseUrl }));

            const stored = readStoredSession();
            if (stored?.access_token && stored?.refresh_token) {
                setStep("already-authed");
                safeSetLoading(false);
                safeSetError(null);
                return;
            }

            // ─── NATIVE PATH: use Capacitor biometric plugin ───
            if (isNative()) {
                setStep("native-biometric-check");
                console.log('[BiometricLogin] native path: checking availability…');

                const nativeBio = await isNativeBiometricAvailable();
                console.log('[BiometricLogin] available:', nativeBio.available, 'biometryType:', nativeBio.biometryType, 'raw:', nativeBio.rawBiometryType);
                if (!nativeBio.available) {
                    throw new Error(
                        "Biometric authentication is not available on this device.",
                    );
                }

                console.log('[BiometricLogin] getting stored credentials…');
                const creds = await getNativeCredentials();
                console.log('[BiometricLogin] creds found:', !!creds);
                if (!creds) {
                    throw new Error(
                        "No biometric credentials found. Please sign in with your password first.",
                    );
                }

                setStep("native-biometric-prompt");
                console.log('[BiometricLogin] prompting biometric (type:', nativeBio.biometryType, ')…');

                try {
                    await promptNativeBiometric("Sign in to Mailander");
                    console.log('[BiometricLogin] biometric prompt succeeded');
                } catch (promptErr: any) {
                    console.error('[BiometricLogin] biometric prompt error:', promptErr?.message, promptErr);
                    throw new Error("Biometric authentication was cancelled.");
                }

                setStep("native-token-refresh");

                // Use stored refresh token to get a new session via backend
                let refreshRes;
                try {
                    refreshRes = await axios.post(
                        `${baseUrl}/auth/refresh-token`,
                        { refresh_token: creds.refreshToken },
                        {
                            headers: { "Content-Type": "application/json" },
                            withCredentials: false,
                            timeout: 10_000,
                            signal: controller.signal,
                        },
                    );
                } catch (refreshErr: any) {
                    // Stored refresh token is stale (revoked/expired) —
                    // clear credentials and prompt password login.
                    console.warn('[BiometricLogin] refresh failed, clearing stale credentials:', refreshErr?.message);
                    const { clearNativeCredentials } = await import('../../../helpers/native-biometric');
                    await clearNativeCredentials().catch(() => {});
                    BiometricsStorage.setBiometricsEnabled(false);
                    BiometricsStorage.setBiometricsType(null);
                    throw new Error(
                        "Your session has expired. Please sign in with your password to re-enable biometrics.",
                    );
                }

                const payload: VerifySuccessPayload = {
                    access_token: refreshRes.data?.access_token,
                    refresh_token: refreshRes.data?.refresh_token,
                    expires_in: refreshRes.data?.expires_in,
                    user: refreshRes.data?.user,
                    raw: refreshRes.data,
                };

                if (!payload.access_token || !payload.refresh_token) {
                    throw new Error(
                        "Session expired. Please sign in with your password.",
                    );
                }

                // Re-store the new refresh token in Keychain so it's fresh
                // for the next logout/re-login cycle.
                try {
                    const { storeNativeCredentials } = await import('../../../helpers/native-biometric');
                    const uid = payload.user?.id || creds.userId;
                    await storeNativeCredentials(uid, payload.refresh_token);
                } catch {
                    // Non-critical — useNativeCredentialSync will also update
                }

                safeSetSuccessPayload(payload);
                setStep("success");

                if (onSuccess) await onSuccess(payload);

                safeSetLoading(false);
                safeSetError(null);
                return;
            }

            // ─── WEB PATH: existing WebAuthn flow ───
            if (!window.isSecureContext) {
                throw new Error("Biometric login requires HTTPS or localhost.");
            }
            if (!("PublicKeyCredential" in window)) {
                throw new Error("WebAuthn is not supported in this browser.");
            }

            setStep("capability-check");

            const [uvpa, cma] = await Promise.all([
                probeUvpa(),
                probeConditionalMediation(),
            ]);
            safeSetDiag((d: any) => ({
                ...d,
                platformAvailable: uvpa,
                conditionalMediationAvailable: cma,
            }));

            setStep("fetch-authentication-options");

            const optionsUrl =
                `${baseUrl}/webauthn/generate-authentication-options`;
            safeSetDiag((d: any) => ({
                ...d,
                lastRequest: {
                    url: optionsUrl,
                    method: "POST",
                    ts: new Date().toISOString(),
                },
            }));

            const clientDiag = await buildDiag();

            const optionsRes = await axios.post(
                optionsUrl,
                { email: BiometricsStorage.getEmail() || null, diag: clientDiag },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-Request-Id": reqId,
                    },
                    withCredentials: false,
                    timeout: 10_000, // 10s — fail fast if backend unreachable
                    signal: controller.signal,
                },
            );

            safeSetDiag((d: any) => ({
                ...d,
                lastRequest: {
                    url: optionsUrl,
                    method: "POST",
                    ts: new Date().toISOString(),
                    status: optionsRes.status,
                },
            }));

            const optionsJSON = optionsRes.data?.optionsJSON;
            const challengeId = optionsRes.data?.challenge_id;
            if (!optionsJSON || !challengeId) {
                throw new Error(
                    `Backend response missing optionsJSON/challenge_id. Got keys: ${
                        optionsRes.data
                            ? Object.keys(optionsRes.data).join(", ")
                            : "null"
                    }`,
                );
            }

            setStep("start-authentication");

            // ✅ CRITICAL: Only use conditional UI when an eligible input exists AND is focused.
            // Otherwise it can hang indefinitely.
            // NEVER use conditional UI on mobile platforms (iOS/Android)
            const canUseConditionalUI = cma === true && preferConditionalUI &&
                !platform.isMobile &&
                hasFocusedWebauthnInput();

            const authPromise = startAuthentication({
                optionsJSON,
                ...(canUseConditionalUI ? { useBrowserAutofill: true } : {}),
            });

            // Guard against "hang" paths with platform-appropriate timeout.
            const biometricName = getBiometricName(platform.isIos, platform.isAndroid);
            const timeoutMessage = platform.isMobile 
                ? `Biometric prompt did not appear. Please ensure ${biometricName} is enabled in your device settings.`
                : 'Biometric prompt did not appear. Tap "Use biometrics" to try again.';
            
            const assertion = await withTimeout(
                authPromise,
                effectiveTimeout,
                timeoutMessage,
            );

            setStep("verify-authentication");

            const verifyUrl = `${baseUrl}/webauthn/verify-authentication`;
            safeSetDiag((d: any) => ({
                ...d,
                lastRequest: {
                    url: verifyUrl,
                    method: "POST",
                    ts: new Date().toISOString(),
                },
            }));

            const verifyRes = await axios.post(
                verifyUrl,
                {
                    email: null,
                    challenge_id: challengeId,
                    clientDiag,
                    credential: assertion,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-Request-Id": reqId,
                    },
                    withCredentials: false,
                    timeout: 10_000, // 10s — fail fast if backend unreachable
                    signal: controller.signal,
                },
            );

            safeSetDiag((d: any) => ({
                ...d,
                lastRequest: {
                    url: verifyUrl,
                    method: "POST",
                    ts: new Date().toISOString(),
                    status: verifyRes.status,
                },
            }));

            const payload: VerifySuccessPayload = {
                access_token: verifyRes.data?.access_token,
                refresh_token: verifyRes.data?.refresh_token,
                expires_in: verifyRes.data?.expires_in,
                user: verifyRes.data?.user,
                raw: verifyRes.data,
            };

            if (!payload.access_token || !payload.refresh_token) {
                throw new Error(
                    "Biometric login succeeded but tokens are missing in response.",
                );
            }

            safeSetSuccessPayload(payload);
            setStep("success");

            if (onSuccess) await onSuccess(payload);

            safeSetLoading(false);
            safeSetError(null);
        } catch (e) {
            const info = safeErr(e);

            const aborted = (info.kind === "axios" &&
                (e as any)?.code === "ERR_CANCELED") ||
                (e instanceof DOMException && e.name === "AbortError");

            safeSetLoading(false);

            if (aborted) {
                setStep("aborted");
                safeSetError(null);
                return;
            }

            if (info.kind === "axios") {
                const status = info.status;
                const data: any = info.data;

                if (status === 429) {
                    const backoffMs = computeBackoffMsFrom429(info.headers);
                    setSsNumber(SS_BLOCK_UNTIL, nowMs() + backoffMs);
                    const seconds = Math.ceil(backoffMs / 1000);
                    setStep("rate-limited-429");
                    safeSetError(
                        `Too many requests. Try again in ${seconds}s.`,
                    );
                    return;
                }

                // E6: Truncate long error messages (mobile overflow)
                let msg = typeof data?.detail === "string"
                    ? data.detail
                    : Array.isArray(data?.detail)
                    ? data.detail.map((d: any) => d?.msg || String(d)).join('; ')
                    : typeof data?.message === "string"
                    ? data.message
                    : info.message;
                if (msg && msg.length > 200) {
                    msg = msg.slice(0, 197) + '…';
                }

                safeSetError(status ? `(${status}) ${msg}` : msg);
            } else {
                safeSetError(
                    (info as any).message ?? "Biometric login failed.",
                );
            }
            
            // Add platform-specific guidance to error messages
            if (platform.isMobile) {
                const platformName = getPlatformName(platform.isIos, platform.isAndroid);
                const biometricName = getBiometricName(platform.isIos, platform.isAndroid);
                
                setStep("mobile-error");
                if ((info as any).message?.includes('NotAllowed')) {
                    safeSetError(
                        `Biometric authentication was cancelled or not allowed. Please ensure ${biometricName} is enabled on your ${platformName} device.`,
                    );
                } else if ((info as any).message?.includes('timeout')) {
                    safeSetError(
                        `Biometric prompt timed out. Please ensure ${biometricName} is working properly on your device.`,
                    );
                }
            }
        } finally {
            inFlightRef.current = false;
            abortRef.current = null;
        }
    }, [
        buildDiag,
        onSuccess,
        preferConditionalUI,
        effectiveTimeout,
        platform,
        safeSetDiag,
        safeSetError,
        safeSetLoading,
        safeSetSuccessPayload,
        setStep,
    ]);

    useEffect(() => {
        if (!autoStart) return;
        if (didAutoStartRef.current) return;
        didAutoStartRef.current = true;
        void run();
    }, [autoStart, run]);

    return { loading, error, diag, successPayload, start: run };
}
